import { AxiosPromise, AxiosResponse } from 'axios'
import { format } from 'date-fns'
import { saveAs } from 'file-saver'
import { useConfiguracoes } from 'hooks/configuracoes'
import { useServerTime } from 'hooks/useServerTime'
import { useCallback } from 'react'
import { isUndefinedOrNull } from 'util/checks'
import { previewFile } from 'util/previewFile'

import {
  useHandleImpressaoError,
  UseHandleImpressaoErrorProps,
  UseHandleImpressaoErrorResult,
} from './useHandleImpressaoError'
import { readFileError } from './util-impressao'

type DownloadType = 'download' | 'preview'
const SUFFIXES = ['.pdf', '.csv']
export const reportFormatDate = 'yyyy-MM-dd_hhmm'

export interface HandleImpressaoPathProps<T> {
  apiPath: (values: T) => AxiosPromise<Blob | ArrayBuffer>
  values: T
  file: string
  downloadType?: DownloadType
  dateFormat?: string
}

export interface HandleImpressaoProps {
  blob: Blob
  file: string
  dateFormat?: string
}

type UseHandleImpressaoProps<E> = UseHandleImpressaoErrorProps<E>

export interface UseHandleImpressaoResult<T, E> extends UseHandleImpressaoErrorResult<E> {
  handleImpressao: (props: HandleImpressaoProps) => void
  handleImpressaoPath: (props: HandleImpressaoPathProps<T>) => Promise<AxiosResponse<Blob | ArrayBuffer>>
}

const getBlobType = (fileName: string): object => {
  const extension = getExtension(fileName)
  if (extension === '.csv') return { type: 'text/csv' }
  return { type: 'application/pdf' }
}

const getExtension = (fileName: string): string | undefined => {
  const file = fileName.toLowerCase()
  return SUFFIXES.find((suffix) => file.endsWith(suffix))
}

const removeExtension = (fileName: string): string => fileName.substring(0, fileName.lastIndexOf('.'))

const addSufix = (fileName: string, suffix: string): string => {
  const fileExtension = getExtension(fileName)
  return fileExtension ? `${removeExtension(fileName)}${suffix}${fileExtension}` : `${fileName}${suffix}.pdf`
}

const addTreinamentoSuffix = (fileName: string, isTreinamento: boolean): string => {
  if (isTreinamento) return addSufix(fileName, '_treinamento')
  return fileName
}

const addDateTime = (fileName: string, date: Date, dateFormat: string): string =>
  addSufix(fileName, '_' + format(date, dateFormat))

export function useHandleImpressao<T, E = unknown>(props?: UseHandleImpressaoProps<E>): UseHandleImpressaoResult<T, E> {
  const {
    configuracoes: { treinamento },
  } = useConfiguracoes()

  const { getServerTimeNow } = useServerTime()
  const { handleImpressaoError } = useHandleImpressaoError<E>(props)

  const handleImpressaoPath = useCallback(
    async <T>(props: Readonly<HandleImpressaoPathProps<T>>) => {
      const { apiPath, values, file, dateFormat, downloadType = 'preview' } = props
      try {
        const response = await apiPath(values)
        const type = getBlobType(file)
        const blob = new Blob([response.data], type)
        const filename = isUndefinedOrNull(dateFormat) ? file : addDateTime(file, getServerTimeNow(), dateFormat)
        if (downloadType === 'download') {
          saveAs(blob, addTreinamentoSuffix(filename, treinamento))
        } else {
          previewFile(blob, addTreinamentoSuffix(filename, treinamento))
        }
        return response
      } catch (error) {
        const readError = await readFileError(error)
        throw readError
      }
    },
    [getServerTimeNow, treinamento]
  )

  const handleImpressao = useCallback(
    (props: Readonly<HandleImpressaoProps>) => {
      const { blob, file, dateFormat = reportFormatDate } = props
      const filename = isUndefinedOrNull(dateFormat) ? file : addDateTime(file, getServerTimeNow(), dateFormat)
      saveAs(blob, addTreinamentoSuffix(filename, treinamento))
    },
    [getServerTimeNow, treinamento]
  )

  return {
    handleImpressao,
    handleImpressaoPath,
    handleImpressaoError,
  }
}
